// src/features/s-curve/components/SCurveChart.tsx
import React, { useMemo } from 'react';
import { ResponsiveContainer, ComposedChart, Line, Area, CartesianGrid, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import type { SCurveRow } from '../api/sCurve.api';

function betaPdf(x: number, a: number, b: number) {
    if (x <= 0 || x >= 1) return 0;
    return Math.pow(x, a - 1) * Math.pow(1 - x, b - 1);
}

function betaCdfApprox(x: number, a: number, b: number, total: number) {
    const n = 160;
    const dx = x / n;
    let area = 0;
    let prev = betaPdf(0, a, b);

    for (let i = 1; i <= n; i++) {
        const xi = dx * i;
        const cur = betaPdf(xi, a, b);
        area += (prev + cur) * 0.5 * dx;
        prev = cur;
    }

    const cdf = area / (total || 1);
    return Math.min(1, Math.max(0, cdf));
}

function betaTotalApprox(a: number, b: number) {
    const N = 200;
    const dd = 1 / N;
    let A = 0;
    let p = betaPdf(0, a, b);

    for (let i = 1; i <= N; i++) {
        const xx = dd * i;
        const c = betaPdf(xx, a, b);
        A += (p + c) * 0.5 * dd;
        p = c;
    }
    return A || 1;
}

function fmtPct(v: any) {
    const n = Number(v);
    if (!Number.isFinite(n)) return '0%';
    return `${n.toFixed(1)}%`;
}

const OVERLAY_COLORS = [
    '#64748b', // slate
    '#0ea5e9', // sky
    '#22c55e', // green
    '#f97316', // orange
    '#a855f7', // purple
    '#ef4444', // red
    '#14b8a6', // teal
];

export default function SCurveChart({
    rows,
    height = 320,
    showIncrementInTooltip = false,
    showLatest = false,
    showBenchmark = true,
    alpha = 2.2,
    beta = 2.2,
    showFitted = false,
    fittedAlpha,
    fittedBeta,
    overallDistribution,
    selectedDistribution,
    showOverallDistribution = true,
    showSelectedDistribution = true,
    // overlays,
    // overlayVisible,
    seriesVisible,
}: {
    rows: SCurveRow[];
    height?: number;
    showIncrementInTooltip?: boolean;
    showLatest?: boolean;
    showBenchmark?: boolean;
    alpha?: number;
    beta?: number;
    showFitted?: boolean;
    fittedAlpha?: number;
    fittedBeta?: number;
    overallDistribution?: { label: string; values: number[]; axis?: 'cum' | 'inc' };
    selectedDistribution?: { label: string; values: number[]; axis?: 'cum' | 'inc' };
    showOverallDistribution?: boolean;
    showSelectedDistribution?: boolean;
    // overlays?: Array<{ id: string; label: string; values: number[]; axis?: 'cum' | 'inc' }>;
    // overlayVisible?: Record<string, boolean>;
    seriesVisible?: Partial<{
        pvCum: boolean;
        pvForecastCum: boolean;
        evForecastCum: boolean;
        evApprovedCum: boolean;
        evLatestCum: boolean;
        benchCum: boolean;
        pvInc: boolean;
        evApprovedInc: boolean;
    }>;
}) {
    const xTickFormatter = (value: string, index: number) => {
        if (!rows || rows.length === 0) return value;

        const lastIndex = rows.length - 1;

        // 처음과 마지막은 항상 표시
        if (index === 0 || index === lastIndex) {
            return value;
        }

        // 중간은 듬성듬성 (대략 6~8개만 보이게)
        const step = Math.ceil(rows.length / 8);
        return index % step === 0 ? value : '';
    };

    const data = useMemo(() => {
        const n = rows?.length ?? 0;
        if (!n) return [];

        const totalBench = betaTotalApprox(alpha, beta);

        const hasFit =
            showFitted &&
            Number.isFinite(fittedAlpha) &&
            Number.isFinite(fittedBeta) &&
            (fittedAlpha as number) > 0 &&
            (fittedBeta as number) > 0;

        const totalFit = hasFit ? betaTotalApprox(fittedAlpha as number, fittedBeta as number) : 1;

        return rows.map((r, idx) => {
            const t = n === 1 ? 1 : idx / (n - 1);
            const benchCum = betaCdfApprox(t, alpha, beta, totalBench) * 100;
            const fitCum = hasFit
                ? betaCdfApprox(t, fittedAlpha as number, fittedBeta as number, totalFit) * 100
                : undefined;

            const extra: Record<string, number | undefined> = {};

            // ✅ overall
            if (overallDistribution?.values?.length) {
                const v = overallDistribution.values[idx];
                extra.ov_overallBell = Number.isFinite(v) ? v : undefined;
            }

            // ✅ selected
            if (selectedDistribution?.values?.length) {
                const v = selectedDistribution.values[idx];
                extra.ov_selectedBell = Number.isFinite(v) ? v : undefined;
            }

            return { ...r, benchCum, fitCum, ...extra };
        });
    }, [rows, alpha, beta, showFitted, fittedAlpha, fittedBeta, overallDistribution, selectedDistribution]);

    const overlayLabelByKey = useMemo(() => {
        const m = new Map<string, string>();
        if (overallDistribution?.label) m.set('ov_overallBell', overallDistribution.label);
        if (selectedDistribution?.label) m.set('ov_selectedBell', selectedDistribution.label);
        return m;
    }, [overallDistribution?.label, selectedDistribution?.label]);

    return (
        <div style={{ width: '100%', height }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ left: 12, right: 12, top: 8, bottom: 0 }}>
                    {/* <CartesianGrid //strokeDasharray="3 3" /> */}
                    <CartesianGrid
                        strokeDasharray="3 6"
                        vertical={false}
                    />
                    {/* <XAxis dataKey="label" tickMargin={8} interval={xInterval} minTickGap={24} /> */}
                    <XAxis
                        dataKey="label"
                        tickFormatter={xTickFormatter}
                        tickMargin={8}
                        interval={0}
                        minTickGap={20}
                        tick={{ fontSize: 12 }}
                    />
                    {/* <YAxis
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        allowDecimals={false}
                        tickFormatter={(v) => `${v}%`}
                        width={42}
                        tick={{ fontSize: 12 }}
                    /> */}
                    {/* 누적(0~100) */}
                    <YAxis
                        yAxisId="cum"
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        allowDecimals={false}
                        tickFormatter={(v) => `${v}%`}
                        width={42}
                        tick={{ fontSize: 12 }}
                    />

                    {/* 증가(우측) - 자동 스케일 */}
                    <YAxis
                        yAxisId="inc"
                        orientation="right"
                        // domain={[0, 'dataMax + 1']}
                        domain={[0, 15]} // 15% 까지
                        tickCount={4}
                        tickFormatter={(v) => `${v}%`}
                        width={42}
                        tick={{ fontSize: 10 }}
                    />

                    <Tooltip
                        labelFormatter={(_, payload) => {
                            const r = payload?.[0]?.payload as any;
                            return r ? `${r.label} · ${r.date}` : '';
                        }}
                        formatter={(value: any, name: any) => {
                            if (overlayLabelByKey.has(name)) return [fmtPct(value), overlayLabelByKey.get(name)];
                            if (name === 'benchCum') return [fmtPct(value), '표준(누적)'];
                            if (name === 'pvCum') return [fmtPct(value), '계획(PV, 누적)'];
                            if (name === 'pvForecaseCum') return [fmtPct(value), '예측(PV, 누적)'];
                            if (name === 'evApprovedCum') return [fmtPct(value), '확정(EV, 누적)'];
                            if (name === 'evLatestCum') return [fmtPct(value), '잠정(EV, 누적)'];
                            if (name === 'pvInc') return [fmtPct(value), '계획(PV, 증가)'];
                            if (name === 'evApprovedInc') return [fmtPct(value), '확정(EV, 증가)'];
                            if (name === 'evLatestInc') return [fmtPct(value), '잠정(EV, 증가)'];
                            if (name === 'evDeltaCum') return [fmtPct(value), '차이(잠정-확정)'];
                            if (name === 'fitCum') return [fmtPct(value), '추천(AutoFit,누적)'];
                            return [fmtPct(value), name];
                        }}
                        contentStyle={{ fontSize: 12, padding: '6px 8px' }}
                        labelStyle={{ fontSize: 12, fontWeight: 600 }}
                        itemStyle={{ fontSize: 12 }}
                    />
                    <Legend
                        verticalAlign="bottom"
                        align="left"
                        height={18}
                        wrapperStyle={{ fontSize: 12, paddingLeft: 8 }}
                    />

                    {/* PV 누적 */}
                    {seriesVisible?.pvCum !== false ? (
                        <Line
                            yAxisId="cum"
                            type="monotone"
                            dataKey="pvCum"
                            name="계획(PV)"
                            dot={false}
                            activeDot={{ r: 4 }}
                            stroke="#2563eb"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    ) : null}

                    {/* Todo: 예측 모델 제대로 만든 후 살릴 것. */}
                    {/* ✅ PV Forecast 누적 */}
                    {/* {seriesVisible?.pvForecastCum !== false ? (
                        <Line
                            yAxisId="cum"
                            type="monotone"
                            dataKey="pvForecastCum"
                            name="예측(EV기반)"
                            dot={false}
                            stroke="#f97316"
                            //strokeDasharray="10 6"
                            strokeWidth={2.5}
                            // strokeOpacity={0.95}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    ) : null} */}

                    {/* ✅ EV Forecast 누적 */}
                    {/* {seriesVisible?.evForecastCum !== false ? (
                        <Line
                            yAxisId="cum"
                            type="monotone"
                            dataKey="evForecastCum"
                            name="예측(EV기반)"
                            dot={false}
                            stroke="#1af916"
                            //strokeDasharray="10 6"
                            strokeWidth={2.5}
                            // strokeOpacity={0.95}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    ) : null} */}

                    {/* 확정 EV 누적 */}
                    {seriesVisible?.evApprovedCum !== false ? (
                        <Line
                            yAxisId="cum"
                            type="monotone"
                            dataKey="evApprovedCum"
                            name="확정(EV)"
                            dot={false}
                            activeDot={{ r: 4 }}
                            stroke="#f97316"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    ) : null}

                    {/* 잠정 EV 누적 */}
                    {showLatest && seriesVisible?.evLatestCum !== false ? (
                        <Line
                            yAxisId="cum"
                            type="monotone"
                            dataKey="evLatestCum"
                            name="잠정(EV)"
                            dot={false}
                            activeDot={{ r: 4 }}
                            stroke="#f59e0b"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    ) : null}

                    {/* 표준 곡선 */}
                    {showBenchmark && seriesVisible?.benchCum !== false ? (
                        <Line
                            yAxisId="cum"
                            type="monotone"
                            dataKey="benchCum"
                            name="표준(Benchmark)"
                            dot={false}
                            stroke="#6b7280"
                            //strokeDasharray="8 6"
                            strokeWidth={2.2}
                            strokeOpacity={0.9}
                            strokeLinecap="round"
                        />
                    ) : null}

                    {/* ✅ 전체 분포 */}
                    {showOverallDistribution && overallDistribution?.values?.length ? (
                        <>
                            <Area
                                yAxisId={overallDistribution.axis ?? 'inc'}
                                type="monotone"
                                dataKey="ov_overallBell"
                                name="전체 분포"
                                stroke="none"
                                fill="#64748b"
                                fillOpacity={0.14}
                                isAnimationActive={false}
                                legendType="none"
                                tooltipType="none"
                            />
                            <Line
                                yAxisId={overallDistribution.axis ?? 'inc'}
                                type="monotone"
                                dataKey="ov_overallBell"
                                name={overallDistribution.label}
                                dot={false}
                                strokeWidth={1.5}
                                stroke="#64748b"
                                //strokeDasharray="3 6"
                                strokeOpacity={0.35}
                                strokeLinecap="round"
                                isAnimationActive={false}
                                // legendType="none"
                                // tooltipType="none"
                            />
                        </>
                    ) : null}

                    {/* ✅ 선택 분포 */}
                    {showSelectedDistribution && selectedDistribution?.values?.length ? (
                        <>
                            <Area
                                yAxisId={selectedDistribution.axis ?? 'inc'}
                                type="monotone"
                                dataKey="ov_selectedBell"
                                stroke="none"
                                fill="#0ea5e9"
                                fillOpacity={0.22}
                                isAnimationActive={false}
                                legendType="none"
                                tooltipType="none"
                            />
                            <Line
                                yAxisId={selectedDistribution.axis ?? 'inc'}
                                type="monotone"
                                dataKey="ov_selectedBell"
                                name={selectedDistribution.label}
                                dot={false}
                                strokeWidth={1.5}
                                stroke="#0ea5e9"
                                //strokeDasharray="3 6"
                                strokeOpacity={0.35}
                                strokeLinecap="round"
                                isAnimationActive={false}
                                // legendType="none"
                                // tooltipType="none"
                            />
                        </>
                    ) : null}

                    {/* 추천(AutoFit) 곡선 */}
                    {showFitted ? (
                        <Line
                            yAxisId="cum"
                            type="monotone"
                            dataKey="fitCum"
                            name="추천(AutoFit)"
                            dot={false}
                            stroke="#a855f7"
                            //strokeDasharray="6 4"
                            strokeWidth={2.6}
                            strokeOpacity={0.95}
                            strokeLinecap="round"
                        />
                    ) : null}

                    {/* 증가량(툴팁용) */}
                    {showIncrementInTooltip ? (
                        <>
                            {/* 계획 증가 area + line */}
                            {seriesVisible?.pvInc !== false ? (
                                <React.Fragment key="pvInc">
                                    <Area
                                        yAxisId="inc"
                                        type="monotone"
                                        dataKey="pvInc"
                                        stroke="none"
                                        fill="#2563eb"
                                        fillOpacity={0.08}
                                        legendType="none"
                                        isAnimationActive={false}
                                    />
                                    {/* <Line
                                        yAxisId="inc"
                                        type="monotone"
                                        dataKey="pvInc"
                                        name="계획 증가"
                                        dot={false}
                                        stroke="#2563eb"
                                        //strokeDasharray="3 6"
                                        strokeWidth={1.6}
                                        strokeOpacity={0.35}
                                        isAnimationActive={false}
                                    /> */}
                                </React.Fragment>
                            ) : null}

                            {/* 확정 증가 area + line */}
                            {seriesVisible?.evApprovedInc !== false ? (
                                <React.Fragment key="evApprovedInc">
                                    <Area
                                        yAxisId="inc"
                                        type="monotone"
                                        dataKey="evApprovedInc"
                                        stroke="none"
                                        fill="#16a34a"
                                        fillOpacity={0.08}
                                        legendType="none"
                                        isAnimationActive={false}
                                    />
                                    {/* <Line
                                        yAxisId="inc"
                                        type="monotone"
                                        dataKey="evApprovedInc"
                                        name="확정 증가"
                                        dot={false}
                                        stroke="#16a34a"
                                        //strokeDasharray="3 6"
                                        strokeWidth={1.6}
                                        strokeOpacity={0.35}
                                        isAnimationActive={false}
                                    /> */}
                                </React.Fragment>
                            ) : null}

                            {/* 잠정 증가 area + line */}
                            {/* {showLatest ? (
                                <React.Fragment key="evLatestInc">
                                    <Area
                                        yAxisId="inc"
                                        type="monotone"
                                        dataKey="evLatestInc"
                                        stroke="none"
                                        fill="#f59e0b"
                                        fillOpacity={0.08}
                                        isAnimationActive={false}
                                    />
                                    <Line
                                        yAxisId="inc"
                                        type="monotone"
                                        dataKey="evLatestInc"
                                        name="잠정 증가"
                                        dot={false}
                                        stroke="#f59e0b"
                                        //strokeDasharray="3 6"
                                        strokeWidth={1.6}
                                        strokeOpacity={0.35}
                                        isAnimationActive={false}
                                    />
                                </React.Fragment>
                            ) : null} */}
                        </>
                    ) : null}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}
